import {
  importStaffRow,
  importStudentRow,
  PASSWORD_MIN_LENGTH,
  type ImportStaffRow,
  type ImportStudentRow,
} from '@cwy/config/contracts';

export const ROSTER_COLUMNS = ['Name', 'Class code', 'Student ID', 'Password'] as const;

export const STAFF_COLUMNS = [
  'Name',
  'Email',
  'Role',
  'Class code',
  'Subjects',
  'Class teacher',
] as const;

/**
 * What goes in one column, for the guide shown beside each import. Keyed by
 * the column list itself, so a column cannot be added to an import without
 * being explained — the guide and the parser read the same names.
 */
export interface ImportColumnGuide {
  required: boolean;
  example: string;
  help: string;
}

export const ROSTER_COLUMN_GUIDE: Record<(typeof ROSTER_COLUMNS)[number], ImportColumnGuide> = {
  Name: {
    required: true,
    example: 'Aarav Sharma',
    help: 'The student’s full name, as it should appear on reports.',
  },
  'Class code': {
    required: true,
    example: 'cbse-grade-10_a',
    help: 'The class they join. Every class’s code is listed below the paste box, and on Classrooms.',
  },
  'Student ID': {
    required: true,
    example: '2026001',
    help: 'Their sign-in ID — a roll or admission number, unique in your school. Keep it the same every year.',
  },
  Password: {
    required: true,
    example: 'first-term-2026',
    help: `At least ${PASSWORD_MIN_LENGTH} characters. New students change it at first sign-in; a returning student keeps their own.`,
  },
};

export const STAFF_COLUMN_GUIDE: Record<(typeof STAFF_COLUMNS)[number], ImportColumnGuide> = {
  Name: { required: true, example: 'Priya Sharma', help: 'Their full name.' },
  Email: {
    required: true,
    example: 'priya.sharma@yourschool.in',
    help: 'Their work email. They sign in with it at /staff.',
  },
  Role: {
    required: false,
    example: 'Teacher',
    help: 'Teacher or Admin. Left empty, they are a teacher.',
  },
  'Class code': {
    required: false,
    example: 'cbse-grade-10_a',
    help: 'A class they take. One class per row — for each further class, add another row with the same email.',
  },
  Subjects: {
    required: false,
    example: 'Mathematics; Science',
    help: 'What they teach in that class, named as in your curriculum. Separate several with semicolons. Needs a class code.',
  },
  'Class teacher': {
    required: false,
    example: 'Yes',
    help: 'Yes if they look after that class. A teacher is class teacher of one class at most.',
  },
};

const SAMPLE_CODES = ['cbse-grade-10_a', 'cbse-grade-10_b'];

/**
 * A sample student file: the header and a few made-up students, in the
 * school's own class codes when it has any, so the one column nobody can guess
 * is already right. It parses cleanly as it stands (a test holds it to that).
 */
export function studentSample(classCodes: readonly string[]): string[][] {
  const codes = classCodes.length > 0 ? classCodes : SAMPLE_CODES;
  const names = ['Aarav Sharma', 'Diya Nair', 'Kabir Singh', 'Ananya Iyer'];
  return [
    [...ROSTER_COLUMNS],
    ...names.map((name, index) => [
      name,
      codes[index % Math.min(codes.length, 2)]!,
      `202600${index + 1}`,
      'first-term-2026',
    ]),
  ];
}

/**
 * A sample staff file showing each shape a row can take: a teacher across two
 * classes (two rows, one email), a teacher with one subject, one with no
 * classes yet, and an admin. Real class codes and subject names when the
 * school has them.
 */
export function staffSample(
  classCodes: readonly string[],
  subjectNames: readonly string[],
): string[][] {
  const [first, second = first] = classCodes.length > 0 ? classCodes : SAMPLE_CODES;
  const [subjectA, subjectB = subjectA, subjectC = subjectB] =
    subjectNames.length > 0 ? subjectNames : ['Mathematics', 'Science', 'English'];
  return [
    [...STAFF_COLUMNS],
    [
      'Priya Sharma',
      'priya.sharma@yourschool.in',
      'Teacher',
      first!,
      `${subjectA}; ${subjectB}`,
      'Yes',
    ],
    ['Priya Sharma', 'priya.sharma@yourschool.in', 'Teacher', second!, subjectA!, 'No'],
    ['Rahul Verma', 'rahul.verma@yourschool.in', 'Teacher', first!, subjectC!, 'No'],
    ['Neha Kapoor', 'neha.kapoor@yourschool.in', 'Teacher', '', '', ''],
    ['Rohan Mehta', 'rohan.mehta@yourschool.in', 'Admin', '', '', ''],
  ];
}

/** One non-empty line of a CSV or spreadsheet paste, with the line it began on. */
interface DelimitedRecord {
  line: number;
  cells: string[];
}

/**
 * The one tokenizer every roster import reads through: CSV files, CRLF, a
 * leading BOM, quoted fields with escaped quotes, and tab-separated pastes from
 * a spreadsheet. Blank lines are skipped. A malformed quote stops the read and
 * is reported, with every record before it kept — a row must never vanish
 * without a message.
 */
function readDelimited(text: string): { records: DelimitedRecord[]; error: string | null } {
  const source = text.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
  const records: DelimitedRecord[] = [];
  // A tab outside quotes identifies a spreadsheet paste; commas in its cells are data.
  let delimiter = ',';
  let quoted = false;
  for (const char of source.trimStart()) {
    if (char === '"') quoted = !quoted;
    if (!quoted && char === '\t') delimiter = '\t';
    if (!quoted && char === '\n') break;
  }
  let cells: string[] = [];
  let cell = '';
  let line = 1;
  let rowLine = 1;
  let afterQuote = false;
  quoted = false;
  const finishRow = () => {
    cells.push(cell.trim());
    if (cells.some(Boolean)) records.push({ line: rowLine, cells });
    cells = [];
    cell = '';
    afterQuote = false;
    rowLine = line + 1;
  };
  for (let i = 0; i < source.length; i++) {
    const char = source[i]!;
    if (quoted) {
      if (char === '"') {
        if (source[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          quoted = false;
          afterQuote = true;
        }
      } else cell += char;
    } else if (char === delimiter) {
      cells.push(cell.trim());
      cell = '';
      afterQuote = false;
    } else if (char === '\n') finishRow();
    else if (char === '"' && !cell.trim() && !afterQuote) {
      quoted = true;
      cell = '';
    } else if ((afterQuote && char.trim()) || char === '"') {
      return {
        records,
        error: `Row ${rowLine}: unexpected text or quote after a field. Use double quotes around a field containing separators.`,
      };
    } else cell += char;
    if (char === '\n') line++;
  }
  if (quoted) return { records, error: `Row ${rowLine}: a quoted field is not closed.` };
  finishRow();
  return { records, error: null };
}

/** "Row 3: email — Invalid email address", from a contract's first issue. */
function issueOf(line: number, error: { issues: { path: PropertyKey[]; message: string }[] }) {
  const issue = error.issues[0];
  return `Row ${line}: ${String(issue?.path[0] ?? 'value')} — ${issue?.message ?? 'invalid row'}`;
}

/** CSV and spreadsheet pastes share one parser; malformed rows must never disappear. */
export function parseRoster(text: string): { students: ImportStudentRow[]; errors: string[] } {
  const { records, error } = readDelimited(text);
  const students: ImportStudentRow[] = [];
  const errors: string[] = [];
  for (const [index, { line, cells }] of records.entries()) {
    const header =
      index === 0 &&
      /^(name|student|full name)$/i.test(cells[0]!) &&
      /^class(?: code)?$/i.test(cells[1] ?? '');
    if (header) continue;
    if (cells.length !== ROSTER_COLUMNS.length) {
      errors.push(`Row ${line}: expected four columns (name, class code, student ID, password).`);
      continue;
    }
    const [name, classroomCode, loginId, password] = cells;
    const parsed = importStudentRow.safeParse({ name, classroomCode, loginId, password });
    if (parsed.success) students.push(parsed.data);
    else errors.push(issueOf(line, parsed.error));
  }
  if (error) errors.push(error);
  return { students, errors };
}

/** "Teacher" unless the sheet says admin — the role most rows of a staff list are. */
function staffRoleOf(cell: string): ImportStaffRow['role'] | null {
  const value = cell.trim().toLowerCase();
  if (!value || value === 'teacher') return 'TEACHER';
  if (['admin', 'school admin', 'principal'].includes(value)) return 'SCHOOL_ADMIN';
  return null;
}

/** Yes/no as a spreadsheet writes it. */
function yesNoOf(cell: string): boolean | null {
  const value = cell.trim().toLowerCase();
  if (['', 'no', 'n', 'false', '0'].includes(value)) return false;
  if (['yes', 'y', 'true', '1'].includes(value)) return true;
  return null;
}

/**
 * A staff list: name and email, then — all optional — role, the class they
 * take, which subjects in it, and whether they are its class teacher. Trailing
 * columns may be left off entirely, so a plain "name, email" list is a valid
 * import of teachers with no classes yet.
 */
export function parseStaffRoster(text: string): { staff: ImportStaffRow[]; errors: string[] } {
  const { records, error } = readDelimited(text);
  const staff: ImportStaffRow[] = [];
  const errors: string[] = [];
  for (const [index, { line, cells }] of records.entries()) {
    const header =
      index === 0 && /^(name|full name)$/i.test(cells[0]!) && /^e-?mail/i.test(cells[1] ?? '');
    if (header) continue;
    if (cells.length < 2 || cells.length > STAFF_COLUMNS.length) {
      errors.push(
        `Row ${line}: expected a name and an email, then up to four more columns (${STAFF_COLUMNS.slice(2).join(', ').toLowerCase()}).`,
      );
      continue;
    }
    const [
      name,
      email,
      roleCell = '',
      classroomCode = '',
      subjectsCell = '',
      classTeacherCell = '',
    ] = cells;
    const role = staffRoleOf(roleCell);
    if (!role) {
      errors.push(`Row ${line}: role must be Teacher or Admin.`);
      continue;
    }
    const classTeacher = yesNoOf(classTeacherCell);
    if (classTeacher === null) {
      errors.push(`Row ${line}: class teacher must be Yes or No.`);
      continue;
    }
    const parsed = importStaffRow.safeParse({
      name,
      email,
      role,
      classroomCode: classroomCode || undefined,
      // Semicolons need no quoting in a CSV cell; a quoted "Maths, Science" is
      // a list too.
      subjects: subjectsCell
        .split(/[;,|]/)
        .map((subject) => subject.trim())
        .filter(Boolean),
      classTeacher,
    });
    if (parsed.success) staff.push(parsed.data);
    else errors.push(issueOf(line, parsed.error));
  }
  if (error) errors.push(error);
  return { staff, errors };
}

/** One CSV cell, quoted when it holds a separator, a quote or a line break. */
function csvCell(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Rows to CSV text, the inverse of `readDelimited` for anything it can read. */
export function toCsv(rows: readonly (readonly string[])[]): string {
  return rows.map((row) => row.map(csvCell).join(',')).join('\n') + '\n';
}

/** Hand the browser a CSV to save. Built here, so nothing is uploaded to make it. */
export function downloadCsv(fileName: string, rows: readonly (readonly string[])[]): void {
  const url = URL.createObjectURL(new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
